import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { httpPost } from "../helpers/httpMethods";
import "./pay.css";
import tnsLogo from "../../assets/images/appLogoV2.png";
import { useBillsOn } from "../Bills/billsOn";
import "../Bills/billsTouch.css";

// Screens around Paystack: a TNS strip while it's open, then confirming / success / not completed.
// Mounts itself on first use, so nothing else in the app needs to change.
let node = null;
let setter = null;
let pending = null;
let current = null;
let summary = null;

export const setPaySummary = (s) => {
  summary = s;
};

export function payEvent(type, data = {}) {
  // After Paystack reports success, a late "window closed" must not replace the confirmation.
  if (type === "closed" && current && ["paid", "success", "slow"].includes(current.type)) return;
  const next = { type, ...data, summary: data.summary || summary };
  current = next;
  if (!node) {
    node = document.createElement("div");
    document.body.appendChild(node);
    pending = next;
    ReactDOM.render(<PayOverlay />, node);
    return;
  }
  if (setter) setter(next);
  else pending = next;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const naira = (v) => `₦${Math.round(Number(v) || 0).toLocaleString("en-NG")}`;
const Lock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

function PayOverlay() {
  const [s, set] = useState(pending);
  const billsOn = useBillsOn(); // one quiet line after a paper is paid for
  useEffect(() => {
    setter = (v) => set(v);
    if (pending) {
      set(pending);
      pending = null;
    }
    return () => {
      setter = null;
    };
  }, []);

  // Ask the server to confirm with Paystack; only then say "You're in".
  useEffect(() => {
    if (!s || s.type !== "paid") return undefined;
    let alive = true;
    (async () => {
      for (let i = 0; i < 10; i += 1) {
        const r = await httpPost(`payments/confirm/${encodeURIComponent(s.reference)}`, {});
        if (!alive) return;
        const st = r && !r.er && r.data ? r.data.status : null;
        if (st === "paid") {
          current = { ...s, type: "success" };
          set(current);
          return;
        }
        if (st === "failed" || st === "abandoned" || st === "reversed") {
          current = { ...s, type: "closed" };
          set(current);
          return;
        }
        await sleep(2500);
      }
      if (alive) {
        current = { ...s, type: "slow" };
        set(current);
      }
    })();
    return () => {
      alive = false;
    };
  }, [s && s.type, s && s.reference]);

  if (!s) return null;
  const title = s.summary && s.summary.title ? s.summary.title : "Your order";
  const amount = s.summary && s.summary.amount ? naira(s.summary.amount) : s.amount ? naira(s.amount) : "";
  const close = () => {
    current = null;
    summary = null;
    set(null);
  };
  const readNow = () => {
    const done = s.onDone;
    close();
    if (done) done();
  };

  if (s.type === "open") {
    if (window.innerWidth < 720 || window.innerHeight < 800) return null; // Paystack fills small screens
    return (
      <div className="py-strip" role="status" aria-live="polite">
        <span className="py-tile"><img src={tnsLogo} alt="TheNewspaperStand" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", background: "#fff", borderRadius: "inherit", padding: 4, boxSizing: "border-box" }} /></span>
        <span className="py-strip-text"><strong>{title}{amount ? ` · ${amount}` : ""}</strong><span>Paying securely with Paystack</span></span>
        <span className="py-lock"><Lock /></span>
      </div>
    );
  }

  let body = null;
  if (s.type === "paid") {
    body = (
      <>
        <span className="py-spin" aria-hidden="true" />
        <h2 id="py-title" className="py-title">Confirming your payment…</h2>
        <p className="py-text">This usually takes a few seconds. Please keep this page open.</p>
      </>
    );
  } else if (s.type === "success") {
    body = (
      <>
        <span className="py-ok"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg></span>
        <h2 id="py-title" className="py-title">You’re in</h2>
        <div className="py-sum"><span>{title}</span><strong>{amount}</strong></div>
        <button type="button" className="py-btn" onClick={readNow}>Read now</button>
        <button type="button" className="py-btn2" onClick={close}>Close</button>
        {billsOn ? (
          <button type="button" className="bt-line" onClick={() => { close(); window.location.href = "/account/bills"; }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M12 20h.01" /></svg>
            <span>Low on data? Top up in seconds.</span><strong>Buy data</strong>
          </button>
        ) : null}
        {s.reference ? <p className="py-ref">Payment reference: {s.reference}</p> : null}
      </>
    );
  } else if (s.type === "slow") {
    body = (
      <>
        <h2 id="py-title" className="py-title">We’ll finish this shortly</h2>
        <p className="py-text">Paystack has your payment and we’re still confirming it. Bank transfers can take a few minutes. Your access will appear automatically, and you won’t be charged twice.</p>
        <div className="py-sum"><span>{title}</span><strong>{amount}</strong></div>
        <button type="button" className="py-btn" onClick={readNow}>Continue</button>
        {s.reference ? <p className="py-ref">Payment reference: {s.reference}</p> : null}
      </>
    );
  } else if (s.type === "closed") {
    body = (
      <>
        <h2 id="py-title" className="py-title">Payment not completed</h2>
        <p className="py-text">You closed the payment window before paying. Nothing was charged.</p>
        {amount ? <div className="py-sum"><span>{title}</span><strong>{amount}</strong></div> : null}
        <button type="button" className="py-btn" onClick={close}>Back to plans</button>
        <div className="py-note"><strong>Paid by bank transfer?</strong> Transfers can take a few minutes to confirm. Your access will appear as soon as it arrives, and you won’t be charged twice.</div>
      </>
    );
  }
  if (!body) return null;
  return (
    <div className="py-overlay">
      <section className="py-card" role="dialog" aria-modal="true" aria-labelledby="py-title">{body}</section>
    </div>
  );
}
